/**
 * Единая авторизация Eplex (мини-ТЗ P9). Чистые правила: разбор значения куки,
 * проверка внутреннего пути возврата, сборка адреса страницы входа REG.
 *
 * Здесь нет обращений к браузеру — cookie и storage читает `authBrowserState`.
 */

/** Access-токен. Намеренно без HttpOnly (P9 §2.1) — его читает JS фронтов. */
export const AUTH_TOKEN_COOKIE = "eplex_token";

/**
 * Адреса страницы входа здесь больше нет вовсе: его отдаёт бэкенд (Ф1).
 *
 * Раньше он собирался из переменной сборки, и стенд, на котором её забыли задать,
 * молча отправлял людей вводить пароль в ПРОД. Сборка раздаётся всем окружениям
 * одинаковой, поэтому знать свой контур она не может в принципе — знает только сервер.
 */

const BARE_JWT_SEGMENTS = 3;
const PREFIXED_JWT_SEGMENTS = 4;

/**
 * Значение `eplex_token` — либо голый access-JWT (3 сегмента через точку), либо
 * `<префикс маршрутизации>.<jwt>` (4 сегмента): префикс лежит СНАРУЖИ токена.
 *
 * Наличие префикса определяем числом сегментов, а не разбором символов — так
 * просил держатель контракта, и причина практическая: на проде префикс есть ровно
 * у одного клиента из 1031, поэтому ошибка в разборе не проявится месяцами, а потом
 * проявится у единственного клиента, и искать её будут не здесь.
 */
export function isSessionTokenValue(value: string | null | undefined): boolean {
  if (!value) return false;

  const segments = value.split(".");
  if (segments.length !== BARE_JWT_SEGMENTS && segments.length !== PREFIXED_JWT_SEGMENTS) return false;

  return segments.every((segment) => segment.length > 0);
}

/** Длина хвоста куки, по которой мы узнаём «это тот же самый токен». */
const SESSION_FINGERPRINT_LENGTH = 32;

/**
 * Короткий признак конкретного токена — хвост его подписи.
 *
 * Нужен, чтобы помнить «вот ЭТУ сессию бэкенд уже отверг», не храня сам токен и не
 * рискуя тем, что метка переживёт вход заново: у нового токена подпись другая,
 * значит метка перестанет совпадать сама, без всякой уборки (CAB-680).
 */
export function sessionFingerprint(value: string | null | undefined): string | null {
  if (!isSessionTokenValue(value)) return null;

  return (value as string).slice(-SESSION_FINGERPRINT_LENGTH);
}

/**
 * Отрезает префикс маршрутизации: возвращает сам JWT без ведущего сегмента.
 * Нужен там, где токен уходит заголовком на api.eplex.ru.
 */
export function stripRoutingPrefix(value: string): string {
  const segments = value.split(".");
  return segments.length === PREFIXED_JWT_SEGMENTS ? segments.slice(1).join(".") : value;
}

/**
 * `?redirect=` приходит из адресной строки, то есть управляется кем угодно.
 * Пропускаем только внутренний путь.
 *
 * Без этой проверки адрес чужого сайта уехал бы в `return_to`: REG проверит по
 * белому списку СВОЙ редирект и пропустит его как наш, а дальше мы своими руками
 * унесём человека наружу с настоящей страницы входа. Это фишинг с нашей подписью,
 * ровно тот, от которого белый список REG и защищает.
 */
export function sanitizeInternalPath(raw: unknown): string | null {
  if (typeof raw !== "string" || raw.length === 0) return null;
  if (!raw.startsWith("/")) return null;
  // `//evil.com` — протокольно-относительный адрес; `/\evil.com` браузеры
  // нормализуют в него же, поэтому обратный слэш отклоняем в любом месте пути
  if (raw.startsWith("//") || raw.includes("\\")) return null;
  // управляющие символы и пробелы позволяют собрать адрес, который парсер URL
  // и человек читают по-разному
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u0020\u007f]/.test(raw)) return null;

  return raw;
}
